import { useState } from "react";
import { Eye, FileSpreadsheet } from "lucide-react";
import { z } from "zod";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { RoleEnum } from "@/enum/role";
import { useGetStoresResponsiblesOptionsQuery } from "@/services/store/select-options";
import { useLazyGenerateExcelReportQuery, useLazyGetReportPreviewQuery } from "@/services/conciliation/reports";
import { downloadExcel } from "@/utils/download-excel";
import { DAY_LACK_REPORT } from "@/constants/conciliaton-report-types";

import { ReportPreviewTable } from "./report-preview-table";
import PeriodRangeFilter from "components/PeriodRangeFilter";

const reportSchema = z.object({
  startDate: z.string().min(1, "Data de início obrigatória"),
  endDate: z.string().min(1, "Data de fim obrigatória"),
  analist: z.string().optional(),
});

interface ReportGeneratorProps {
  reportType: string;
  title: string;
  description: string;
}

export function ReportGenerator({ reportType, title, description }: ReportGeneratorProps) {
  const { data: responsibleOptions } = useGetStoresResponsiblesOptionsQuery({
    role: RoleEnum.ANALISTA,
  });

  const [trigger, { isFetching: isExporting }] = useLazyGenerateExcelReportQuery();
  const [triggerPreview, { isFetching: isLoadingPreview }] = useLazyGetReportPreviewQuery();

  const [selectedAnalyst, setSelectedAnalyst] = useState("");
  const [previewData, setPreviewData] = useState<{ headers: string[]; data: any[] } | null>(null);

  const supportsPreview = reportType === DAY_LACK_REPORT;

  const getFormData = () => ({
    startDate: period.start ?? "",
    endDate: period.end ?? "",
    analist: selectedAnalyst === "Todos" ? "" : selectedAnalyst,
  });

  const validateForm = () => {
    const formData = getFormData();
    const result = reportSchema.safeParse(formData);

    if (!result.success) {
      toast({ variant: "destructive", description: result.error.errors[0]?.message });
      return false;
    }
    return true;
  };

  const handlePreview = async () => {
    if (!validateForm()) return;

    try {
      const result = await triggerPreview({ ...getFormData(), reportType }).unwrap();
      setPreviewData(result);
    } catch (error) {
      console.error(error);
      toast({ variant: "destructive", description: "Erro ao carregar pré-visualização." });
    }
  };

  const handleExport = async () => {
    if (supportsPreview && !previewData) {
      toast({ variant: "destructive", description: "Visualize os dados antes de exportar." });
      return;
    }

    if (!validateForm()) return;

    try {
      const result = await trigger({ ...getFormData(), reportType }).unwrap();
      downloadExcel(result.excelFile, `${title}.xlsx`);

      const count = previewData?.data.length ?? 0;
      toast({
        variant: "default",
        description: `Excel gerado com ${count} registros, incluindo o status da conciliação.`,
        duration: 3000,
      });
    } catch (error) {
      console.error(error);
      toast({ variant: "destructive", description: "Erro ao gerar o relatório." });
    }
  };

  const canExport = !supportsPreview || previewData !== null;

  const [period, setPeriod] = useState<{ start?: string; end?: string }>({});

  const handlePeriodChange = (start: string | undefined, end: string | undefined) => {
    setPeriod({ start, end });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>

      <CardContent>
        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="period-filter" className="text-sm">Período</Label>
            <PeriodRangeFilter
              start={period.start}
              end={period.end}
              onChange={handlePeriodChange}
              inline
              buttonClassName="w-full h-8 justify-start font-normal"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="analista">Analista</Label>
            <Select value={selectedAnalyst} onValueChange={setSelectedAnalyst}>
              <SelectTrigger id="analista">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Todos">Todos</SelectItem>
                {responsibleOptions?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        </div>

        {/* Botões */}
        <div className="mt-8 flex flex-col items-end gap-2">
          <div className="flex gap-2">
            {supportsPreview && (
              <Button onClick={handlePreview} disabled={isLoadingPreview}>
                {isLoadingPreview ? "Carregando..." : (
                  <>
                    <Eye className="mr-2 h-4 w-4" />
                    Visualizar
                  </>
                )}
              </Button>
            )}

            <Button
              variant={supportsPreview ? "outline" : "default"}
              onClick={handleExport}
              disabled={isExporting || !canExport}
            >
              {isExporting ? "Gerando..." : (
                <>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Exportar para Excel
                </>
              )}
            </Button>
          </div>

          {supportsPreview && (
            <p className="text-xs text-muted-foreground">
              O Excel será gerado com os mesmos dados exibidos abaixo.
            </p>
          )}
        </div>

        {/* Preview */}
        {supportsPreview && previewData && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">
                Pré-visualização do Relatório ({previewData.data.length} registros)
              </h3>
              <span className="text-xs text-muted-foreground">
                Ordenado por Data (decrescente)
              </span>
            </div>
            <ReportPreviewTable
              headers={previewData.headers}
              data={previewData.data}
              isLoading={isLoadingPreview}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ReportGenerator;
